import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router';

import { Button } from '@/components/Button/Button';
import { useDialogFocusReturn } from '@/hooks/useDialogFocusReturn';

import { buildCopyCourseSearch } from './copyCourseParams';
import styles from './CopyCourseDialog.module.css';

interface CopyCourseDialogProps {
  courseId: string;
  courseTitle: string;
  onOpenChange(open: boolean): void;
  open: boolean;
}

function getDefaultCourseName(courseTitle: string) {
  return `Copy of ${courseTitle}`;
}

export function CopyCourseDialog({
  courseId,
  courseTitle,
  onOpenChange,
  open,
}: CopyCourseDialogProps) {
  const navigate = useNavigate();
  const courseNameId = useId();
  const includeSettingsId = useId();
  const defaultCourseName = useMemo(
    () => getDefaultCourseName(courseTitle),
    [courseTitle],
  );
  const [courseName, setCourseName] = useState(defaultCourseName);
  const [includeSettings, setIncludeSettings] = useState(false);
  const trimmedCourseName = courseName.trim();
  // Not autoFocus: that moves focus before Radix keeps it in the dialog, and
  // Tab then reached the page behind (#173).
  const courseNameRef = useRef<HTMLInputElement>(null);
  const focusReturn = useDialogFocusReturn({ initialFocus: courseNameRef });

  useEffect(() => {
    if (!open) {
      setCourseName(defaultCourseName);
      setIncludeSettings(false);
      return;
    }

    setCourseName(defaultCourseName);
    setIncludeSettings(false);
  }, [defaultCourseName, open]);

  function handleCopy() {
    if (!trimmedCourseName) {
      return;
    }

    navigate(
      `/app/create${buildCopyCourseSearch({
        copyFromCourseId: courseId,
        courseName: trimmedCourseName,
        includeSettings,
      })}`,
    );
    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} {...focusReturn}>
          <div className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>Copy Course</Dialog.Title>
              <Dialog.Description asChild>
                <p className={styles.description}>
                  Start a new course using this course as the source.
                </p>
              </Dialog.Description>
            </div>
          </div>

          <div className={styles.body}>
            <label className={styles.field} htmlFor={courseNameId}>
              <span className={styles.label}>Course name</span>
              <input
                autoComplete="off"
                className={styles.input}
                id={courseNameId}
                onChange={(event) => setCourseName(event.target.value)}
                ref={courseNameRef}
                value={courseName}
              />
            </label>

            <label className={styles.checkboxRow} htmlFor={includeSettingsId}>
              <input
                checked={includeSettings}
                className={styles.checkbox}
                id={includeSettingsId}
                onChange={(event) => setIncludeSettings(event.target.checked)}
                type="checkbox"
              />
              <span>Include current question settings and assignments</span>
            </label>

            <p className={styles.helperText}>
              Assignment due dates and visibility settings are NOT copied.
            </p>
          </div>

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button disabled={!trimmedCourseName} onClick={handleCopy}>
              Copy
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
